import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import SpinnerIcon from '../icon/Spinner';
import { SelectRootTest } from '../SearchSelect/SearchSelecTest';
import OHADA_COUNTRIES from '../../constants/ohada-countries.constants';
import { UserDto, CompanyDto } from '@jaam/commons/dtos';

const REQUIRED_SUFFIX = ' *';

const createContactFormSchema = (t: (key: string) => string) =>
  z.object({
    fullName: z.string().min(1, t('help.contact.validation.fullNameRequired')),
    organizationName: z.string().min(1, t('help.contact.validation.organizationNameRequired')),
    activitySector: z.string().min(1, t('help.contact.validation.activitySectorRequired')),
    country: z.string().min(1, t('help.contact.validation.countryRequired')),
    phone: z.string().min(1, t('help.contact.validation.phoneRequired')),
    email: z.string().email(t('help.contact.validation.emailInvalid')),
    topic: z.enum(['support', 'feedback'], {
      required_error: t('help.contact.validation.topicRequired'),
    }),
    message: z.string().min(1, t('help.contact.validation.messageRequired')),
  });

type ContactFormData = z.infer<ReturnType<typeof createContactFormSchema>>;

const capitalizeWords = (str: string): string =>
  str
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

interface ContactFormProps {
  activitySectorOptions?: { value: string | number; label: string }[];
  isLoadingSectors?: boolean;
  user?: UserDto;
  activeCompany?: CompanyDto | null;
  onSubmit?: (data: ContactFormData) => Promise<void>;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  activitySectorOptions: providedOptions,
  isLoadingSectors = false,
  user,
  activeCompany,
  onSubmit: onSubmitProp,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const contactFormSchema = createContactFormSchema(t);

  const activitySectorOptions = useMemo(() => {
    if (providedOptions && providedOptions.length > 0) {
      return providedOptions.map((opt) => ({
        value: String(opt.value),
        label: opt.label,
      }));
    }
    return [
      { value: 'technology', label: t('help.contact.sectors.technology') },
      { value: 'finance', label: t('help.contact.sectors.finance') },
      { value: 'healthcare', label: t('help.contact.sectors.healthcare') },
      { value: 'education', label: t('help.contact.sectors.education') },
      { value: 'other', label: t('help.contact.sectors.other') },
    ];
  }, [providedOptions, t]);

  const defaultValues = useMemo(() => {
    const rawFullName = user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`.trim()
      : user?.firstName || user?.lastName || '';
    const fullName = rawFullName ? capitalizeWords(rawFullName) : '';

    let activitySector = '';
    if (activeCompany?.businessSectorId && activitySectorOptions.length > 0) {
      const matchingSector = activitySectorOptions.find(
        (opt) => String(opt.value) === String(activeCompany.businessSectorId)
      );
      if (matchingSector) {
        activitySector = matchingSector.value;
      }
    }

    return {
      fullName: fullName,
      organizationName: activeCompany?.name || '',
      activitySector: activitySector,
      country: activeCompany?.country || '',
      phone: activeCompany?.phone || '',
      email: user?.email || '',
      topic: 'support' as const,
      message: '',
    };
  }, [user, activeCompany, activitySectorOptions]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const countryOptions = useMemo(
    () =>
      OHADA_COUNTRIES.map((country) => ({
        value: country.value,
        label: t(country.text),
        icon: country.icon,
      })),
    [t],
  );

  const formatCountryLabel = (option: { label: string; icon?: React.ReactNode }) => (
    <div className="flex items-center gap-2">
      {option.icon && <span className="h-6">{option.icon}</span>}
      <span>{option.label}</span>
    </div>
  );

  const onSubmit = async (data: ContactFormData) => {
    setSubmitSuccess(false);
    setIsSubmitting(true);
    try {
      const countryOption = countryOptions.find((opt) => opt.value === data.country);
      const countryName = countryOption?.label || data.country;

      const sectorOption = activitySectorOptions.find((opt) => opt.value === data.activitySector);
      const sectorName = sectorOption?.label || data.activitySector;

      const formDataToSubmit = {
        ...data,
        country: countryName,
        activitySector: sectorName,
      };

      if (onSubmitProp) {
        await onSubmitProp(formDataToSubmit);
      } else {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      setSubmitSuccess(true);
      reset(defaultValues);
    } catch (error: unknown) {
      void error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
     
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 bg-white p-7 rounded-[24px] ">
        <Controller
          name="fullName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              label={`${t('help.contact.fields.fullName') || 'Nom et Prénom'}${REQUIRED_SUFFIX}`}
              error={errors.fullName?.message}
            />
          )}
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <Controller
            name="organizationName"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label={`${t('help.contact.fields.organizationName') || "Nom de l'organisation"}${REQUIRED_SUFFIX}`}
                error={errors.organizationName?.message}
              />
            )}
          />

          <div>
            <Controller
              name="activitySector"
              control={control}
              render={({ field }) => (
                <SelectRootTest
                  value={activitySectorOptions.find((opt) => opt.value === field.value) || null}
                  onChange={(option) => {
                    field.onChange(option ? (option as { value: string; label: string }).value : '');
                  }}
                  options={activitySectorOptions}
                  placeholder={`${t('help.contact.fields.selectActivitySector')}${REQUIRED_SUFFIX}`}
                  error={errors.activitySector?.message}
                  isClearable
                  isLoading={isLoadingSectors}
                />
              )}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <SelectRootTest
                  value={countryOptions.find((opt) => opt.value === field.value) || null}
                  onChange={(option) => {
                    field.onChange(option ? (option as { value: string; label: string }).value : '');
                  }}
                  options={countryOptions}
                  placeholder={`${t('help.contact.fields.selectCountry')}${REQUIRED_SUFFIX}`}
                  error={errors.country?.message}
                  isClearable
                  formatOptionLabel={(option: { label: string; icon?: React.ReactNode }) => formatCountryLabel(option)}
                />
              )}
            />
          </div>

          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label={`${t('help.contact.fields.phone') || 'Phone'}${REQUIRED_SUFFIX}`}
                error={errors.phone?.message}
                maskType="phone"
              />
            )}
          />
        </div>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="email"
              label={`${t('help.contact.fields.email') || 'Email'}${REQUIRED_SUFFIX}`}
              error={errors.email?.message}
            />
          )}
        />

        <div>
          <label className="mb-2 text-sm font-semibold text-neutral-20">
            {t('help.contact.fields.topic') || 'Topic'}
            {REQUIRED_SUFFIX}
          </label>
          <Controller
            name="topic"
            control={control}
            render={({ field }) => (
              <div className="flex gap-6 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="support"
                    checked={field.value === 'support'}
                    onChange={() => field.onChange('support')}
                    className="w-5 h-5 text-primary"
                  />
                  <span className="text-sm text-neutral-20">
                    {t('help.contact.topics.support') || 'Support'}
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="feedback"
                    checked={field.value === 'feedback'}
                    onChange={() => field.onChange('feedback')}
                    className="w-5 h-5 text-primary"
                  />
                  <span className="text-sm text-neutral-20">
                    {t('help.contact.topics.feedback') || 'Feedback'}
                  </span>
                </label>
              </div>
            )}
          />
          {errors.topic && (
            <p className="mt-1 text-xs text-red-500">{errors.topic.message}</p>
          )}
        </div>

        <Controller
          name="message"
          control={control}
          render={({ field }) => (
            <div>
            
              <textarea
                {...field}
                rows={6}
                placeholder={`${t('help.contact.fields.messagePlaceholder') || 'Your message'}${REQUIRED_SUFFIX}`}
                className="w-full px-3 py-2 text-base border bg-neutral-98 border-neutral-90 rounded-xl focus:border-primary focus:outline-none disabled:bg-neutral-98 disabled:text-neutral-80"
              />
              {errors.message && (
                <p className="mt-1 text-xs text-red-500">{errors.message.message}</p>
              )}
            </div>
          )}
        />

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full"
        >
          {isSubmitting ? (
            <>
              <SpinnerIcon width={20} height={20} className="text-white" />
              {t('help.contact.submitting') || 'Envoi en cours...'}
            </>
          ) : (
            t('help.contact.submit') || 'Envoyer'
          )}
        </Button>
      </form>
    </div>
  );
};
